<script lang="ts">
import type { RecordPagination } from '@/types'
import { type PropType, defineComponent } from 'vue'

type FetchPaginationFunction<T = any> = (
  page: number
) => Promise<RecordPagination<T>>

/**
 * AppPagination
 *
 * Usage:
 * ```vue
 * <AppPagination :fetch-fn="fetchFunction">
 *   <template #default="{ items }">
 *     <!-- items will be typed based on fetchFunction's return type -->
 *   </template>
 * </AppPagination>
 * ```
 *
 * @see https://github.com/vuejs/vue/issues/9692
 */
export default defineComponent({
  inheritAttrs: false,
  props: {
    fetchFn: {
      type: Function as PropType<FetchPaginationFunction<any>>,
      required: true,
    },
    preButtons: {
      type: Boolean,
      default: true,
    },
    postButtons: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fetching: false,
      currentPage: 1,
      totalPages: 1,
      total: 0,
      perPage: 0,
      hasMoreItems: true,
      items: null,
    }
  },
  async mounted() {
    await this.goToPage()
  },
  methods: {
    async goToPage(page: number = 1) {
      this.fetching = true

      const paginatedItems = await this.fetchFn(page)

      this.currentPage = paginatedItems.current_page
      this.totalPages = paginatedItems.last_page
      this.total = paginatedItems.total
      this.perPage = paginatedItems.per_page
      this.hasMoreItems = !!paginatedItems.next_page_url
      this.items = paginatedItems.data

      this.fetching = false
    },
    async back() {
      return await this.goToPage(Math.max(this.currentPage - 1, 1))
    },
    async forward() {
      return await this.goToPage(
        Math.min(this.currentPage + 1, this.totalPages)
      )
    },
  },
})
</script>

<template>
  <div v-if="preButtons" class="flex justify-between">
    <button
      :disabled="currentPage === 1"
      type="button"
      class="flex items-center"
      :class="{
        'text-[#8f8f8f] hover:cursor-not-allowed': currentPage === 1,
      }"
      @click="back"
    >
      <AppIcon :icon="Icons.ArrowLeft" size="xs" /> Prev.
    </button>
    <button
      :disabled="!hasMoreItems"
      type="button"
      class="flex items-center"
      :class="{
        'text-[#8f8f8f] hover:cursor-not-allowed': !hasMoreItems,
      }"
      @click="forward"
    >
      Next
      <AppIcon :icon="Icons.ArrowLeft" size="xs" class="rotate-180" />
    </button>
  </div>

  <div v-if="fetching" class="spinner mx-auto !block" />

  <div :class="{ invisible: fetching }" v-bind="$attrs">
    <slot :items />
  </div>

  <div v-if="postButtons" class="flex justify-between">
    <button
      :disabled="currentPage === 1"
      type="button"
      class="flex items-center"
      :class="{
        'text-[#8f8f8f] hover:cursor-not-allowed': currentPage === 1,
      }"
      @click="back"
    >
      <AppIcon :icon="Icons.ArrowLeft" size="xs" /> Prev.
    </button>
    <button
      :disabled="!hasMoreItems"
      type="button"
      class="flex items-center"
      :class="{
        'text-[#8f8f8f] hover:cursor-not-allowed': !hasMoreItems,
      }"
      @click="forward"
    >
      Next
      <AppIcon :icon="Icons.ArrowLeft" size="xs" class="rotate-180" />
    </button>
  </div>
</template>
